import { Switch } from 'antd';
import Barchart from 'components/molecules/Barchart';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { formatNumWithKMBAlt, scaleMaxValue } from 'utils/helperMethods';
import { useRef } from 'react';
import { calculateContainerHeight } from 'utils/helperMethods';
import WaterFallChartIndividualised from 'components/molecules/WaterFallChartIndividualised';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import useGrowthMarginBridgesData from './hooks/useGrowthMarginBridgesData';
import Loader from 'components/molecules/Loader';
import { isEmpty } from 'lodash';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';

const GrowthBridgesChart = ({ setSelectedBar }: { setSelectedBar: (bar: string) => void }) => {
    const chartRef = useRef<any>(null);
    const chartHeight = calculateContainerHeight(2.3);
    const containerWidth = useContainerWidth({
        containerId: 'growth-margin-bridges-waterfall-chart-infinite-scroll',
        buffer: 300,
    });

    const {
        waterFallChartData: profitGrowthBridgesData,
        currentYearData,
        previousYearData,
        isPending,
        isError,
        isSuccess,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        min: waterfallMin,
        max: waterfallMax,
    } = useGrowthMarginBridgesData();

    const minGraphWidth = 100;
    const currentDataLength = profitGrowthBridgesData?.length ?? 0;
    const currentContainerWidth = containerWidth;

    const currentGraphWidth =
        currentContainerWidth / currentDataLength < minGraphWidth
            ? minGraphWidth
            : currentContainerWidth / currentDataLength;

    if (isPending) {
        return (
            <div>
                <p className="text-lg font-semibold">{'CBI Profit Growth'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                <p className="text-lg font-semibold">{'CBI Profit Growth'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox />
                </div>
            </div>
        );
    }

    if (!isPending && isEmpty(profitGrowthBridgesData)) {
        return (
            <div>
                <p className="text-lg font-semibold">{'CBI Profit Growth'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox msg={'No Data'} />
                </div>
            </div>
        );
    }
    console.log('profitGrowthBridgesData', { profitGrowthBridgesData, currentYearData, previousYearData });

    return (
        <>
            <div className="flex flex-row items-center justify-between">
                <p className="text-lg font-semibold">{'CBI Profit Growth'}</p>
                <div className="flex flex-row gap-x-2 items-center">
                    <div className="flex flex-row gap-1 items-center">
                        <p>{'Abs'}</p>
                        <Switch defaultChecked={false} onChange={() => {}} />
                        <p>{'%'}</p>
                    </div>
                    <div className="flex justify-end items-center">
                        <span className="flex gap-2">
                            <CopyToClipBoard refOfComponent={chartRef} />
                        </span>
                    </div>
                </div>
            </div>
            {/* Legend */}
            <div className="items-center flex flex-row justify-between my-2 mb-4">
                <>
                    <div className="text-sm">
                        <p>{'Change'}</p>
                    </div>
                    <div className="flex flex-row gap-2 text-xs">
                        <div className="bg-[#4D388C] w-3 h-2 self-center" />
                        <p>{'Current and Last Year'}</p>
                        <div className="bg-[#42A531] w-3 h-2 self-center" />
                        <p>{'Positive'}</p>
                        <div className="bg-[#CA3D3D] w-3 h-2 self-center" />
                        <p>{'Negative'}</p>
                    </div>
                    <div />
                </>
            </div>

            {/* Chart */}
            <div className="flex mt-2 flex-row">
                {/* Y Axis */}
                <div className="flex">
                    <Barchart
                        padding={[0, 0, -2, 0]}
                        barWidth={40}
                        xAxisConfig={true}
                        xAxisLabelFormatter={(val) => {
                            return val;
                        }}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`growth-margin-bridges-y-axis`}
                        fields={['brand_name', 'totalProfit', 'type']}
                        minBound={waterfallMin}
                        maxBound={scaleMaxValue(20, waterfallMax)}
                        width={50}
                        colorKey={'type'}
                        YGrid={false}
                        yAxisConfig={true}
                        yAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMBAlt(val);
                        }}
                    />
                </div>
                {/* Last Year Chart */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -8, 0]}
                        barWidth={40}
                        xAxisConfig={true}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[
                            {
                                brand_name: 'Year Ago',
                                totalProfit: previousYearData,
                                type: '#5F49BC',
                            },
                        ]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`growth-margin-bridges-last-year-chart`}
                        fields={['brand_name', 'totalProfit', 'type']}
                        minBound={waterfallMin}
                        maxBound={scaleMaxValue(20, waterfallMax)}
                        width={100}
                        colorKey={'type'}
                        yAxisConfig={true}
                        yAxisLabelFormatter={(val: any) => {
                            return '';
                        }}
                    />
                    <p className="text-center">{'LY'}</p>
                </div>

                {/* WaterFall Chart */}
                <div
                    id="growth-margin-bridges-waterfall-chart-infinite-scroll"
                    className="flex-1 flex overflow-x-auto max-w-full"
                    ref={chartRef}
                >
                    {profitGrowthBridgesData.map((_datum) => {
                        return (
                            <div
                                className="hover:bg-black/5"
                                key={`growth-margin-bridges-waterfall-chart-${_datum.attributeDesc}`}
                                onClick={() => {
                                    setSelectedBar(_datum.attributeDesc);
                                }}
                            >
                                <WaterFallChartIndividualised
                                    containerId={`growth-margin-bridges-waterfall-chart-${_datum.attributeDesc}`}
                                    key={`growth-margin-bridges-waterfall-chart-${_datum.attributeDesc}`}
                                    chartHeight={chartHeight}
                                    xAxisField="attributeDesc"
                                    yAxisField="profit_growth_absolute"
                                    colorField="trend"
                                    minBound={waterfallMin}
                                    maxBound={scaleMaxValue(20, waterfallMax)}
                                    dataSet={[_datum]}
                                    chartWidth={currentGraphWidth}
                                    toolTipLabel={'Profit Growth'}
                                />
                                <p className="text-center">{_datum.attributeDesc}</p>
                            </div>
                        );
                    })}
                </div>

                {/* Current Total Chart */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -8, 0]}
                        barWidth={40}
                        xAxisConfig={true}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[
                            {
                                brand_name: 'Current Year',
                                totalProfit: currentYearData,
                                type: '#5F49BF',
                            },
                        ]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`growth-margin-bridges-current-year-chart`}
                        fields={['brand_name', 'totalProfit', 'type']}
                        minBound={waterfallMin}
                        maxBound={scaleMaxValue(20, waterfallMax)}
                        width={100}
                        colorKey={'type'}
                        YGrid={true}
                        yAxisConfig={true}
                    />
                    <p className="text-center">{'CY'}</p>
                </div>
            </div>
        </>
    );
};

export default GrowthBridgesChart;
